import { assertValidAnimationMeta, type AnimationMeta } from '../anim/schema';
import { validateAudioManifest, type AudioManifest } from '../audio/manifest';
import { versionedAssetUrl } from './version';

/**
 * Asset registry + runtime loaders (spec.md §N.2). Canonical pixel-art metadata and the
 * SFX manifest are owner-authored JSON served from `/assets/...` (mirrored into
 * `frontend/public/assets/` from the root `assets/` tree). The runtime fetches and
 * **validates** them; a malformed file fails fast.
 *
 * Entities without a final spritesheet PNG keep rendering through the procedural fallback,
 * while accepted art can be dropped in entity-by-entity without changing gameplay code.
 */

export interface EntityAsset {
  id: string;
  metaUrl: string;
  /** PNG location used when art is dropped in; missing now ⇒ placeholder. */
  imageUrl: string;
  /** Placeholder body color + short label, used until real art arrives. */
  color: string;
  label: string;
}

export type MonsterAssetId = 'monster_001' | 'monster_002' | 'monster_003';

export function monsterAssetIdForFloor(floor: number): MonsterAssetId {
  if (floor >= 8) return 'monster_003';
  if (floor >= 5) return 'monster_002';
  return 'monster_001';
}

export type EnvironmentTileKey = 'floor' | 'cracked' | 'grave' | 'edge' | 'boundary' | 'surround';
export type EnvironmentFloorBandKey = 'f1' | 'f2' | 'f3';
export type EnvironmentRoomPlateKey = EnvironmentFloorBandKey;
export type EnvironmentSurroundPanelBandKey = 'floor1' | 'floor2' | 'floor3';
export type EnvironmentMarkerPropKey = 'ferryStep' | 'descentStair';
export type PickupAssetKey = 'goldCache' | 'oilFlask' | 'torchBundle' | 'commonWeapon' | 'commonRelic' | 'itemChest';
export type ItemIconAssetKey = 'commonWeapon' | 'commonArmor' | 'commonTrinket' | 'commonLantern' | 'commonRelic';
export type UiIconAssetKey = 'hp' | 'lantern' | 'burden' | 'gold' | 'ferryPass';
export type EffectSpriteId =
  | 'vfx_slash_gravelight_01'
  | 'vfx_hit_impact_gravebone_01'
  | 'vfx_death_puff_gravebound_01'
  | 'vfx_pickup_sparkle_01'
  | 'vfx_extraction_seal_01';

export interface EnvironmentSurroundAssets {
  textures: Record<EnvironmentFloorBandKey, string>;
  props?: string;
  ferryman?: string;
}

export interface LoadedEnvironmentSurroundAssets {
  textures: Partial<Record<EnvironmentFloorBandKey, HTMLImageElement>>;
  props?: HTMLImageElement;
  ferryman?: HTMLImageElement;
}

export type EnvironmentSurroundPanels = Record<EnvironmentSurroundPanelBandKey, string[]>;
export type LoadedEnvironmentSurroundPanels = Partial<Record<EnvironmentSurroundPanelBandKey, HTMLImageElement[]>>;
export type EnvironmentMarkerProps = Record<EnvironmentMarkerPropKey, string>;
export type LoadedEnvironmentMarkerProps = Partial<Record<EnvironmentMarkerPropKey, HTMLImageElement>>;
export type PickupAssets = Record<PickupAssetKey, string>;
export type LoadedPickupAssets = Partial<Record<PickupAssetKey, HTMLImageElement>>;
export type ItemIconAssets = Record<ItemIconAssetKey, string>;
export type UiIconAssets = Record<UiIconAssetKey, string>;

export interface EffectSpriteAsset {
  imageUrl: string;
  metaUrl: string;
}

export interface EffectSpriteMeta {
  version: 1;
  image: string;
  frameWidth: number;
  frameHeight: number;
  frames: number[];
  fps: number;
  loop: false;
  durationMs?: number;
  scale?: number;
}

export type EffectSpriteValidationResult =
  | { valid: true; meta: EffectSpriteMeta }
  | { valid: false; errors: string[] };

export interface LoadedEffectSprite {
  meta: EffectSpriteMeta;
  image: HTMLImageElement;
}

export type EffectSpriteAssets = Record<EffectSpriteId, EffectSpriteAsset>;
export type LoadedEffectSprites = Partial<Record<EffectSpriteId, LoadedEffectSprite>>;

export interface EnvironmentTilesetAsset {
  id: string;
  /** PixelLab source tile size in pixels. Runtime draw size follows the current canvas cell size. */
  sourceTileSize: number;
  tiles: Record<EnvironmentTileKey, string>;
  /** Optional baked walkable-floor room plates for the 5x5 playable room area. */
  roomPlates?: Record<EnvironmentRoomPlateKey, string>;
  /** Optional richer art for the non-walkable surround outside the room plate. */
  surroundAssets?: EnvironmentSurroundAssets;
  /** Optional panel-sized scenic art for the non-walkable surround outside the room plate. */
  surroundPanels?: EnvironmentSurroundPanels;
  /** Optional one-tile interactive marker props; missing images fall back to glyphs. */
  markerProps?: EnvironmentMarkerProps;
}

export interface LoadedEnvironmentTileset {
  id: string;
  sourceTileSize: number;
  tiles: Partial<Record<EnvironmentTileKey, HTMLImageElement>>;
  roomPlates?: Partial<Record<EnvironmentRoomPlateKey, HTMLImageElement>>;
  surroundAssets?: LoadedEnvironmentSurroundAssets;
  surroundPanels?: LoadedEnvironmentSurroundPanels;
  markerProps?: LoadedEnvironmentMarkerProps;
}

export const ENTITY_ASSETS: Record<string, EntityAsset> = {
  player: {
    id: 'player',
    metaUrl: '/assets/sprites/player/player.anim.json',
    imageUrl: '/assets/sprites/player/player.png',
    color: '#e8a13c',
    label: 'PLR',
  },
  monster_001: {
    id: 'monster_001',
    metaUrl: '/assets/sprites/monsters/monster_001/monster_001.anim.json',
    imageUrl: '/assets/sprites/monsters/monster_001/monster_001.png',
    color: '#c46a5a',
    label: 'MON',
  },
  monster_002: {
    id: 'monster_002',
    metaUrl: '/assets/sprites/monsters/monster_002/monster_002.anim.json',
    imageUrl: '/assets/sprites/monsters/monster_002/monster_002.png',
    color: '#9a7464',
    label: 'OSR',
  },
  monster_003: {
    id: 'monster_003',
    metaUrl: '/assets/sprites/monsters/monster_003/monster_003.anim.json',
    imageUrl: '/assets/sprites/monsters/monster_003/monster_003.png',
    color: '#586070',
    label: 'WRD',
  },
};

export const PLAYER_SKIN_ASSETS: Record<string, EntityAsset> = {
  player_skin_waystation_pilgrim_01: {
    id: 'player_skin_waystation_pilgrim_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_waystation_pilgrim_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_waystation_pilgrim_01.png',
    color: '#c2b38b',
    label: 'PLG',
  },
  player_skin_graveway_scout_01: {
    id: 'player_skin_graveway_scout_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_graveway_scout_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_graveway_scout_01.png',
    color: '#a86d38',
    label: 'SCT',
  },
  player_skin_crypt_surveyor_01: {
    id: 'player_skin_crypt_surveyor_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_crypt_surveyor_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_crypt_surveyor_01.png',
    color: '#546f69',
    label: 'SRV',
  },
  player_skin_blackwater_acolyte_01: {
    id: 'player_skin_blackwater_acolyte_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_blackwater_acolyte_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_blackwater_acolyte_01.png',
    color: '#2e7f85',
    label: 'BWA',
  },
  player_skin_graveknight_harness_01: {
    id: 'player_skin_graveknight_harness_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_graveknight_harness_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_graveknight_harness_01.png',
    color: '#5d6764',
    label: 'GNK',
  },
  player_skin_ashwing_herald_01: {
    id: 'player_skin_ashwing_herald_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_ashwing_herald_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_ashwing_herald_01.png',
    color: '#b49258',
    label: 'AWH',
  },
  player_skin_blackhorn_warden_01: {
    id: 'player_skin_blackhorn_warden_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_blackhorn_warden_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_blackhorn_warden_01.png',
    color: '#23252a',
    label: 'BHW',
  },
  player_skin_reliquary_exarch_01: {
    id: 'player_skin_reliquary_exarch_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_reliquary_exarch_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_reliquary_exarch_01.png',
    color: '#caa243',
    label: 'REX',
  },
  player_skin_obol_sovereign_01: {
    id: 'player_skin_obol_sovereign_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_obol_sovereign_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_obol_sovereign_01.png',
    color: '#d8b64f',
    label: 'OBS',
  },
  player_skin_wagerbound_ferryman_01: {
    id: 'player_skin_wagerbound_ferryman_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_wagerbound_ferryman_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_wagerbound_ferryman_01.png',
    color: '#3f4f5f',
    label: 'WBF',
  },
  player_skin_mourncandle_sexton_01: {
    id: 'player_skin_mourncandle_sexton_01',
    metaUrl: '/assets/sprites/player/skins/player_skin_mourncandle_sexton_01.anim.json',
    imageUrl: '/assets/sprites/player/skins/player_skin_mourncandle_sexton_01.png',
    color: '#6f8f5f',
    label: 'SXT',
  },
};

export const CATACOMBS_001_TILESET: EnvironmentTilesetAsset = {
  id: 'catacombs_001',
  sourceTileSize: 64,
  tiles: {
    floor: '/assets/sprites/environment/catacombs_001/floor.png',
    cracked: '/assets/sprites/environment/catacombs_001/cracked.png',
    grave: '/assets/sprites/environment/catacombs_001/grave.png',
    edge: '/assets/sprites/environment/catacombs_001/edge_shadow.png',
    boundary: '/assets/sprites/environment/catacombs_001/boundary.png',
    surround: '/assets/sprites/environment/catacombs_001/surround.png',
  },
  roomPlates: {
    f1: '/assets/sprites/environment/catacombs_001/room_plate_f1.png',
    f2: '/assets/sprites/environment/catacombs_001/room_plate_f2.png',
    f3: '/assets/sprites/environment/catacombs_001/room_plate_f3.png',
  },
  surroundAssets: {
    textures: {
      f1: '/assets/sprites/environment/catacombs_001/surround_texture_f1.png',
      f2: '/assets/sprites/environment/catacombs_001/surround_texture_f2.png',
      f3: '/assets/sprites/environment/catacombs_001/surround_texture_f3.png',
    },
    props: '/assets/sprites/environment/catacombs_001/surround_props_01.png',
    ferryman: '/assets/sprites/environment/catacombs_001/surround_ferryman_01.png',
  },
  surroundPanels: {
    floor1: [
      '/assets/sprites/environment/catacombs_001/surround_panel_f1_a.png',
      '/assets/sprites/environment/catacombs_001/surround_panel_f1_b.png',
    ],
    floor2: [
      '/assets/sprites/environment/catacombs_001/surround_panel_f2_a.png',
      '/assets/sprites/environment/catacombs_001/surround_panel_f2_b.png',
    ],
    floor3: [
      '/assets/sprites/environment/catacombs_001/surround_panel_f3_a.png',
      '/assets/sprites/environment/catacombs_001/surround_panel_f3_b.png',
    ],
  },
  markerProps: {
    ferryStep: '/assets/sprites/environment/props/prop_ferry_step_01.png',
    descentStair: '/assets/sprites/environment/props/prop_descent_stair_01.png',
  },
};

export const ENVIRONMENT_TILESETS = {
  catacombs_001: CATACOMBS_001_TILESET,
};

export const ITEM_PICKUP_ASSETS: PickupAssets = {
  goldCache: '/assets/sprites/items/pickups/pickup_gold_cache_01.png',
  oilFlask: '/assets/sprites/items/pickups/pickup_oil_flask_01.png',
  torchBundle: '/assets/sprites/items/pickups/pickup_torch_bundle_01.png',
  commonWeapon: '/assets/sprites/items/pickups/pickup_common_weapon_01.png',
  commonRelic: '/assets/sprites/items/pickups/pickup_common_relic_01.png',
  itemChest: '/assets/sprites/items/pickups/pickup_item_chest_01.png',
};

export const ITEM_ICON_ASSETS: ItemIconAssets = {
  commonWeapon: ITEM_PICKUP_ASSETS.commonWeapon,
  commonArmor: '/assets/sprites/items/icons/item_common_armor_01.png',
  commonTrinket: '/assets/sprites/items/icons/item_common_trinket_01.png',
  commonLantern: '/assets/sprites/items/icons/item_common_lantern_01.png',
  commonRelic: ITEM_PICKUP_ASSETS.commonRelic,
};

export const UI_ICON_ASSETS: UiIconAssets = {
  hp: '/assets/sprites/ui/ui_hp_01.png',
  lantern: '/assets/sprites/ui/ui_lantern_01.png',
  burden: '/assets/sprites/ui/ui_burden_01.png',
  gold: '/assets/sprites/ui/ui_gold_01.png',
  ferryPass: '/assets/sprites/ui/ui_ferry_pass_01.png',
};

export const EFFECT_SPRITE_ASSETS: EffectSpriteAssets = {
  vfx_slash_gravelight_01: {
    imageUrl: '/assets/sprites/effects/vfx_slash_gravelight_01.png',
    metaUrl: '/assets/sprites/effects/vfx_slash_gravelight_01.vfx.json',
  },
  vfx_hit_impact_gravebone_01: {
    imageUrl: '/assets/sprites/effects/vfx_hit_impact_gravebone_01.png',
    metaUrl: '/assets/sprites/effects/vfx_hit_impact_gravebone_01.vfx.json',
  },
  vfx_death_puff_gravebound_01: {
    imageUrl: '/assets/sprites/effects/vfx_death_puff_gravebound_01.png',
    metaUrl: '/assets/sprites/effects/vfx_death_puff_gravebound_01.vfx.json',
  },
  vfx_pickup_sparkle_01: {
    imageUrl: '/assets/sprites/effects/vfx_pickup_sparkle_01.png',
    metaUrl: '/assets/sprites/effects/vfx_pickup_sparkle_01.vfx.json',
  },
  vfx_extraction_seal_01: {
    imageUrl: '/assets/sprites/effects/vfx_extraction_seal_01.png',
    metaUrl: '/assets/sprites/effects/vfx_extraction_seal_01.vfx.json',
  },
};

export const AUDIO_MANIFEST_URL = '/assets/audio/audio.manifest.json';

/**
 * A baked default metadata used only when a fetch fails (e.g., offline). It mirrors the
 * standard 5-state layout so the stage/preview always has a valid, animatable asset.
 */
export const DEFAULT_META: AnimationMeta = {
  version: 1,
  image: 'placeholder.png',
  frameWidth: 32,
  frameHeight: 32,
  scale: 2,
  defaultState: 'idle',
  states: {
    idle: { frames: [0, 1, 2, 3], fps: 4, loop: true },
    walk: { frames: [4, 5, 6, 7], fps: 8, loop: true },
    attack: { frames: [8, 9, 10, 11, 12], fps: 12, loop: false, events: [{ frame: 10, type: 'hitFrame' }] },
    hit: { frames: [13, 14], fps: 12, loop: false },
    death: { frames: [15, 16, 17, 18, 19, 20], fps: 8, loop: false, holdLastFrame: true },
  },
};

/** Fetch + validate an animation metadata JSON; falls back to {@link DEFAULT_META} on any failure. */
export async function loadAnimMeta(url: string): Promise<AnimationMeta> {
  try {
    const res = await fetch(versionedAssetUrl(url));
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    const json = await res.json();
    return assertValidAnimationMeta(json, url);
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.info(`[anim] could not load ${url} (${String(err)}); using default placeholder metadata`);
    }
    return DEFAULT_META;
  }
}

/** Fetch + validate the SFX manifest; returns an empty manifest on failure (audio simply no-ops). */
export async function loadAudioManifest(url: string = AUDIO_MANIFEST_URL): Promise<AudioManifest> {
  try {
    const res = await fetch(versionedAssetUrl(url));
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    const json = await res.json();
    const result = validateAudioManifest(json);
    if (!result.valid) {
      if (typeof console !== 'undefined') console.warn(`[audio] invalid manifest ${url}:\n${result.errors.join('\n')}`);
      return {};
    }
    return result.manifest;
  } catch (err) {
    if (typeof console !== 'undefined') console.info(`[audio] could not load ${url} (${String(err)}); audio disabled`);
    return {};
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load image: ${url}`));
    img.src = versionedAssetUrl(url);
  });
}

export async function loadPickupAssets(assets: PickupAssets = ITEM_PICKUP_ASSETS): Promise<LoadedPickupAssets> {
  const loaded: LoadedPickupAssets = {};
  await Promise.all(
    Object.entries(assets).map(async ([key, url]) => {
      try {
        loaded[key as PickupAssetKey] = await loadImage(url);
      } catch (err) {
        if (typeof console !== 'undefined') {
          console.info(`[pickup] could not load ${url} (${String(err)}); using glyph fallback for pickup "${key}"`);
        }
      }
    }),
  );
  return loaded;
}

const isPositiveInt = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v > 0;

const isNonNegativeInt = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0;

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

export function validateEffectSpriteMeta(input: unknown): EffectSpriteValidationResult {
  const errors: string[] = [];
  if (!isObject(input)) return { valid: false, errors: ['metadata must be an object'] };

  if (input.version !== 1) errors.push(`version must be 1 (got ${String(input.version)})`);
  if (typeof input.image !== 'string' || input.image.length === 0) errors.push('image must be a non-empty string');
  if (!isPositiveInt(input.frameWidth)) errors.push('frameWidth must be a positive integer');
  if (!isPositiveInt(input.frameHeight)) errors.push('frameHeight must be a positive integer');
  if (!Array.isArray(input.frames) || input.frames.length < 4 || input.frames.length > 8) {
    errors.push('frames must contain 4-8 frame indices');
  } else if (!input.frames.every(isNonNegativeInt)) {
    errors.push('frames must contain only non-negative integers');
  }
  if (!(typeof input.fps === 'number' && input.fps > 0)) errors.push('fps must be a positive number');
  if (input.loop !== false) errors.push('loop must be false for brief VFX');
  if (input.durationMs !== undefined && !(typeof input.durationMs === 'number' && input.durationMs > 0)) {
    errors.push('durationMs must be a positive number when present');
  }
  if (input.scale !== undefined && !(typeof input.scale === 'number' && input.scale > 0)) {
    errors.push('scale must be a positive number when present');
  }

  if (errors.length > 0) return { valid: false, errors };
  return { valid: true, meta: input as unknown as EffectSpriteMeta };
}

async function loadEffectSpriteMeta(url: string): Promise<EffectSpriteMeta> {
  const res = await fetch(versionedAssetUrl(url));
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const json = await res.json();
  const result = validateEffectSpriteMeta(json);
  if (!result.valid) throw new Error(`Invalid ${url}:\n  - ${result.errors.join('\n  - ')}`);
  return result.meta;
}

export async function loadEffectSprites(assets: EffectSpriteAssets = EFFECT_SPRITE_ASSETS): Promise<LoadedEffectSprites> {
  const loaded: LoadedEffectSprites = {};
  await Promise.all(
    Object.entries(assets).map(async ([id, asset]) => {
      try {
        const meta = await loadEffectSpriteMeta(asset.metaUrl);
        loaded[id as EffectSpriteId] = { meta, image: await loadImage(asset.imageUrl) };
      } catch (err) {
        if (typeof console !== 'undefined') {
          console.info(`[vfx] could not load ${asset.metaUrl} / ${asset.imageUrl} (${String(err)}); using code-driven fallback for "${id}"`);
        }
      }
    }),
  );
  return loaded;
}

export async function loadEnvironmentTileset(asset: EnvironmentTilesetAsset): Promise<LoadedEnvironmentTileset | null> {
  const loaded: Partial<Record<EnvironmentTileKey, HTMLImageElement>> = {};
  const loadedRoomPlates: Partial<Record<EnvironmentRoomPlateKey, HTMLImageElement>> = {};
  const loadedSurroundTextures: Partial<Record<EnvironmentFloorBandKey, HTMLImageElement>> = {};
  const loadedSurroundPanels: LoadedEnvironmentSurroundPanels = {};
  const loadedMarkerProps: LoadedEnvironmentMarkerProps = {};
  let loadedSurroundProps: HTMLImageElement | undefined;
  let loadedSurroundFerryman: HTMLImageElement | undefined;
  await Promise.all(
    Object.entries(asset.tiles).map(async ([key, url]) => {
      try {
        loaded[key as EnvironmentTileKey] = await loadImage(url);
      } catch (err) {
        if (typeof console !== 'undefined') {
          console.info(`[env] could not load ${url} (${String(err)}); using flat fallback for "${key}"`);
        }
      }
    }),
  );
  await Promise.all(
    Object.entries(asset.roomPlates ?? {}).map(async ([key, url]) => {
      try {
        loadedRoomPlates[key as EnvironmentRoomPlateKey] = await loadImage(url);
      } catch (err) {
        if (typeof console !== 'undefined') {
          console.info(`[env] could not load ${url} (${String(err)}); using tile fallback for room plate "${key}"`);
        }
      }
    }),
  );
  await Promise.all(
    Object.entries(asset.surroundAssets?.textures ?? {}).map(async ([key, url]) => {
      try {
        loadedSurroundTextures[key as EnvironmentFloorBandKey] = await loadImage(url);
      } catch (err) {
        if (typeof console !== 'undefined') {
          console.info(`[env] could not load ${url} (${String(err)}); using legacy surround fallback for "${key}"`);
        }
      }
    }),
  );
  if (asset.surroundAssets?.props) {
    try {
      loadedSurroundProps = await loadImage(asset.surroundAssets.props);
    } catch (err) {
      if (typeof console !== 'undefined') {
        console.info(`[env] could not load ${asset.surroundAssets.props} (${String(err)}); skipping surround props`);
      }
    }
  }
  if (asset.surroundAssets?.ferryman) {
    try {
      loadedSurroundFerryman = await loadImage(asset.surroundAssets.ferryman);
    } catch (err) {
      if (typeof console !== 'undefined') {
        console.info(`[env] could not load ${asset.surroundAssets.ferryman} (${String(err)}); skipping Ferryman surround patch`);
      }
    }
  }
  await Promise.all(
    Object.entries(asset.surroundPanels ?? {}).map(async ([key, urls]) => {
      const images = await Promise.all(
        urls.map(async (url) => {
          try {
            return await loadImage(url);
          } catch (err) {
            if (typeof console !== 'undefined') {
              console.info(`[env] could not load ${url} (${String(err)}); skipping surround panel "${key}"`);
            }
            return null;
          }
        }),
      );
      const loadedImages = images.filter((image): image is HTMLImageElement => image !== null);
      if (loadedImages.length > 0) loadedSurroundPanels[key as EnvironmentSurroundPanelBandKey] = loadedImages;
    }),
  );
  await Promise.all(
    Object.entries(asset.markerProps ?? {}).map(async ([key, url]) => {
      try {
        loadedMarkerProps[key as EnvironmentMarkerPropKey] = await loadImage(url);
      } catch (err) {
        if (typeof console !== 'undefined') {
          console.info(`[env] could not load ${url} (${String(err)}); using glyph fallback for marker "${key}"`);
        }
      }
    }),
  );
  const hasTiles = Object.keys(loaded).length > 0;
  const hasRoomPlates = Object.keys(loadedRoomPlates).length > 0;
  const hasSurroundAssets =
    Object.keys(loadedSurroundTextures).length > 0 || Boolean(loadedSurroundProps) || Boolean(loadedSurroundFerryman);
  const hasSurroundPanels = Object.keys(loadedSurroundPanels).length > 0;
  const hasMarkerProps = Object.keys(loadedMarkerProps).length > 0;
  return hasTiles || hasRoomPlates || hasSurroundAssets || hasSurroundPanels || hasMarkerProps
    ? {
      id: asset.id,
      sourceTileSize: asset.sourceTileSize,
      tiles: loaded,
      ...(hasRoomPlates ? { roomPlates: loadedRoomPlates } : {}),
      ...(hasSurroundAssets
        ? {
          surroundAssets: {
            textures: loadedSurroundTextures,
            ...(loadedSurroundProps ? { props: loadedSurroundProps } : {}),
            ...(loadedSurroundFerryman ? { ferryman: loadedSurroundFerryman } : {}),
          },
        }
        : {}),
      ...(hasSurroundPanels ? { surroundPanels: loadedSurroundPanels } : {}),
      ...(hasMarkerProps ? { markerProps: loadedMarkerProps } : {}),
    }
    : null;
}
